import {
  Box,
  Paper,
  Title,
  Text,
  TextInput,
  Button,
  Flex,
  Select,
  SimpleGrid,
  Textarea,
  Checkbox,
  Stepper,
  Alert,
} from "@mantine/core";
import { IconMapPin, IconWorld, IconId } from "@tabler/icons-react";
import { useState, useMemo, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { countriesData } from "../../components/shared/constants/general.const";
import trpc from "../../shared/utils/trpc";
import { handleTrpcError } from "../../shared/utils/trpc/handletrpcError.util";
import { successNotification } from "../../shared/utils/successNotification";
import "react-international-phone/style.css";
import { PhoneInput } from "react-international-phone";
import { SignupFlowLayout } from "./SignupFlowLayout";
import {
  GT_SIGNUP_COMPANY_ID_KEY,
  GT_SIGNUP_CONTINUATION_KEY,
  GT_SIGNUP_META_KEY,
  GT_SIGNUP_UPLOAD_TOKEN_KEY,
  type SignupRegistrationMeta,
} from "./signupFlowStorage";

export default function SignupCompanyDetailsPage() {
  const navigate = useNavigate();

  const [hydrated, setHydrated] = useState(false);
  const [continuationToken, setContinuationToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [sameAsRegistered, setSameAsRegistered] = useState(false);

  const [formData, setFormData] = useState({
    designation: "",
    phoneNumber: "",
    phoneCountryCode: "",
    country: "",
    city: "",
    registrationNumber: "",
    registrationIdType: null as string | null,
    registeredAddress: "",
    currentAddress: "",
  });

  useEffect(() => {
    setContinuationToken(sessionStorage.getItem(GT_SIGNUP_CONTINUATION_KEY));
    setHydrated(true);
  }, []);

  const isIndia = formData.country === "India";

  const isFormValid =
    formData.designation.trim() !== "" &&
    formData.phoneNumber.trim() !== "" &&
    formData.country !== "" &&
    formData.city.trim() !== "" &&
    formData.registrationNumber.trim() !== "" &&
    (isIndia ? formData.registrationIdType !== null && formData.registrationIdType !== "" : true) &&
    formData.registeredAddress.trim() !== "" &&
    (sameAsRegistered || formData.currentAddress.trim() !== "");

  const countryOptions = useMemo(() => {
    return countriesData.countryGroups.map((group) => ({
      group: group.group,
      items: group.items.map((item) => item.name),
    }));
  }, []);

  const getRegistrationIdType = (headquartersCountry: string, selectedType?: string) => {
    if (headquartersCountry === "India" && selectedType) {
      return selectedType;
    }
    if (!headquartersCountry) return "Registration Number";
    for (const group of countriesData.countryGroups) {
      const countryItem = group.items.find((item) => item.name === headquartersCountry);
      if (countryItem) {
        return countryItem.registrationName;
      }
    }
    return "Registration Number";
  };

  const getRegistrationPlaceholder = (headquartersCountry: string, selectedType?: string) => {
    if (headquartersCountry === "India") {
      return selectedType ? `Enter ${selectedType}` : "Select Registration Type first";
    }
    return `Enter ${getRegistrationIdType(headquartersCountry)}`;
  };

  const handleRegisteredAddressChange = (value: string) => {
    setFormData((prev) => ({
      ...prev,
      registeredAddress: value,
      currentAddress: sameAsRegistered ? value : prev.currentAddress,
    }));
  };

  const handleSameAsRegisteredChange = (checked: boolean) => {
    setSameAsRegistered(checked);
    setFormData((prev) => ({
      ...prev,
      currentAddress: checked ? prev.registeredAddress : "",
    }));
  };

  const handleSubmit = async () => {
    if (!continuationToken || !isFormValid) return;

    setLoading(true);
    try {
      const result = await trpc.auth.completeSignupCompanyProfile.mutate({
        continuationToken,
        userDesignation: formData.designation,
        userPhoneNumber: `${formData.phoneCountryCode}${formData.phoneNumber}`,
        headquartersCountry: formData.country,
        headquartersCity: formData.city,
        registrationNumber: formData.registrationNumber,
        registrationIdType: (formData.registrationIdType || undefined) as any,
        companyRegisteredAddress: formData.registeredAddress,
        currentAddress: sameAsRegistered ? formData.registeredAddress : formData.currentAddress,
      });

      const meta: SignupRegistrationMeta = {
        companyName: result.companyName,
        email: result.email,
        headquartersCity: result.headquartersCity,
        headquartersCountry: result.headquartersCountry,
      };

      sessionStorage.setItem(GT_SIGNUP_UPLOAD_TOKEN_KEY, result.token);
      sessionStorage.setItem(GT_SIGNUP_COMPANY_ID_KEY, result.companyId);
      sessionStorage.setItem(GT_SIGNUP_META_KEY, JSON.stringify(meta));
      sessionStorage.removeItem(GT_SIGNUP_CONTINUATION_KEY);

      successNotification({
        title: "Company details saved",
        message: "Upload your documents on the next step to finish registration.",
      });

      navigate("/signup/documents");
    } catch (err) {
      handleTrpcError(err);
    } finally {
      setLoading(false);
    }
  };

  if (!hydrated) {
    return (
      <SignupFlowLayout>
        <Paper p="xl" radius="lg" maw={900} mx="auto" style={{ border: "1px solid #E5E7EB" }}>
          <Text ta="center">Loading…</Text>
        </Paper>
      </SignupFlowLayout>
    );
  }

  if (!continuationToken) {
    return (
      <SignupFlowLayout>
        <Paper p="xl" radius="lg" maw={900} mx="auto" style={{ border: "1px solid #E5E7EB" }}>
          <Alert color="yellow" title="Continue from email or log in">
            Open the verification link from your email to continue registration. If you already verified, log in — we will
            take you back here if your profile is not finished yet.
          </Alert>
          <Flex justify="center" gap="md" mt="lg">
            <Button component={Link} to="/signup" variant="default">
              Start over
            </Button>
            <Button component={Link} to="/login">
              Log in
            </Button>
          </Flex>
        </Paper>
      </SignupFlowLayout>
    );
  }

  return (
    <SignupFlowLayout>
      <Paper p="xl" radius="lg" maw={900} mx="auto" style={{ border: "1px solid #E5E7EB" }}>
        <Stepper active={1} mb="xl">
          <Stepper.Step label="Account" description="Verify email" />
          <Stepper.Step label="Company details" />
          <Stepper.Step label="Documents" />
        </Stepper>

        <Title order={4} mb="md" c="slate.7" style={{ borderBottom: "1px solid #eee", paddingBottom: "8px" }}>
          Company details
        </Title>
        <Text fz="sm" c="slate.6" mb="lg">
          Your email is verified. Add your company information so we can verify your business.
        </Text>

        <Title order={5} mb="sm" c="slate.7">
          Contact
        </Title>
        <SimpleGrid cols={{ base: 1, sm: 2 }} mb="md">
          <TextInput
            label="Designation"
            placeholder="Enter designation"
            required
            value={formData.designation}
            onChange={(e) => setFormData({ ...formData, designation: e.currentTarget.value })}
          />
          <Box>
            <Text fz="sm" fw={500} mb={3}>
              Phone Number{" "}
              <Text span c="red">
                *
              </Text>
            </Text>
            <PhoneInput
              defaultCountry="in"
              value={`${formData.phoneCountryCode}${formData.phoneNumber}`}
              onChange={(phone, meta) => {
                const countryCode = meta.country?.dialCode ? `+${meta.country.dialCode}` : "";
                const nationalNumber = phone.replace(`+${meta.country?.dialCode || ""}`, "").trim();
                setFormData((prev) => ({ ...prev, phoneCountryCode: countryCode, phoneNumber: nationalNumber }));
              }}
              style={{ width: "100%" }}
              inputStyle={{
                width: "100%",
                height: "36px",
                fontSize: "14px",
                border: "1px solid var(--mantine-color-gray-3)",
                borderLeft: "none",
                borderRadius: "0 var(--mantine-radius-md) var(--mantine-radius-md) 0",
                outline: "none",
              }}
              countrySelectorStyleProps={{
                buttonStyle: {
                  height: "36px",
                  border: "1px solid var(--mantine-color-gray-3)",
                  borderRadius: "var(--mantine-radius-md) 0 0 var(--mantine-radius-md)",
                  padding: "0 12px",
                  backgroundColor: "white",
                },
                dropdownStyleProps: {
                  style: {
                    backgroundColor: "white",
                    boxShadow: "0 4px 12px #00000026",
                    border: "1px solid var(--mantine-color-gray-3)",
                    zIndex: 1000,
                  },
                },
              }}
            />
          </Box>
        </SimpleGrid>

        <Title order={5} mb="sm" c="slate.7" mt="lg">
          Business registration & location
        </Title>

        <SimpleGrid cols={{ base: 1, sm: 2 }} mb="md">
          <Select
            label="Headquarters Country"
            placeholder="Enter country"
            required
            searchable
            value={formData.country}
            onChange={(value) => setFormData({ ...formData, country: value || "", registrationIdType: null })}
            data={countryOptions}
            leftSection={<IconWorld size={18} />}
          />

          <TextInput
            label="Headquarters City"
            placeholder="Enter city"
            required
            value={formData.city}
            onChange={(e) => setFormData({ ...formData, city: e.currentTarget.value })}
            leftSection={<IconMapPin size={18} />}
          />
        </SimpleGrid>

        <SimpleGrid cols={{ base: 1, sm: 2 }} mb="md">
          {isIndia ? (
            <Select
              label="Registration ID Type"
              placeholder="Select type"
              required
              data={[
                { value: "GST", label: "GST" },
                { value: "CIN", label: "CIN" },
                { value: "UDM", label: "UDM (Udyam)" },
              ]}
              value={formData.registrationIdType}
              onChange={(value) => setFormData({ ...formData, registrationIdType: value || null })}
            />
          ) : (
            <TextInput
              label={getRegistrationIdType(formData.country, formData.registrationIdType ?? undefined)}
              placeholder={getRegistrationPlaceholder(formData.country, formData.registrationIdType ?? undefined)}
              required
              value={formData.registrationNumber}
              onChange={(e) => setFormData({ ...formData, registrationNumber: e.currentTarget.value })}
              leftSection={<IconId size={18} />}
            />
          )}
          {isIndia && (
            <TextInput
              label={getRegistrationIdType(formData.country, formData.registrationIdType ?? undefined)}
              placeholder={getRegistrationPlaceholder(formData.country, formData.registrationIdType ?? undefined)}
              required
              value={formData.registrationNumber}
              onChange={(e) => setFormData({ ...formData, registrationNumber: e.currentTarget.value })}
              leftSection={<IconId size={18} />}
              disabled={!formData.registrationIdType}
            />
          )}
        </SimpleGrid>

        <Textarea
          label="Company Registered Address"
          required
          placeholder="Enter company registered address"
          autosize
          minRows={3}
          value={formData.registeredAddress}
          onChange={(e) => handleRegisteredAddressChange(e.currentTarget.value)}
          mb="md"
        />

        <Checkbox
          label="Company current address is same as registered address"
          checked={sameAsRegistered}
          onChange={(e) => handleSameAsRegisteredChange(e.currentTarget.checked)}
          mb="sm"
        />

        <Textarea
          label="Company Current Address"
          placeholder="Enter company current address"
          autosize
          minRows={3}
          value={formData.currentAddress}
          onChange={(e) => setFormData({ ...formData, currentAddress: e.currentTarget.value })}
          disabled={sameAsRegistered}
          required={!sameAsRegistered}
          mb="xl"
        />

        <Flex justify="flex-end">
          <Button size="md" px="xl" onClick={handleSubmit} loading={loading} disabled={!isFormValid || loading}>
            Next
          </Button>
        </Flex>
      </Paper>
    </SignupFlowLayout>
  );
}
